import { h, setChildren } from "../core/dom.js";
import { navigate } from "../core/router.js";
import { fmtDurationLong, fmtVolume, fmtDistance, parseDate } from "../lib/format.js";
import { icon } from "../lib/icons.js";

// Each bar represents one real session. Metric changes never imply a comparison
// between different exercise types; the selected workout remains explicit.
export function sessionTrend(sessions) {
  const recent = sessions.slice(0, 14).reverse();
  const metrics = [
    { key: "duration_seconds", label: "Time", format: fmtDurationLong },
    { key: "volume_kg", label: "Volume", format: fmtVolume },
    { key: "set_count", label: "Sets", format: value => `${value} set${value === 1 ? "" : "s"}` },
  ];
  if (recent.some(s => s.distance_m > 0)) metrics.push({ key: "distance_m", label: "Distance", format: fmtDistance });
  let metric = recent.some(s => s.volume_kg > 0) ? metrics[1] : metrics[0], selected = recent.length - 1;
  const root = h("div", { class: "card session-trend" });
  const options = h("div", { class: "trend-options", "aria-label": "Chart metric" });
  const plot = h("div", { class: "activity-plot", "aria-label": "Recent workouts" });
  const detail = h("div", { class: "trend-detail", "aria-live": "polite" });
  const scale = h("div", { class: "trend-scale" });
  const date = s => parseDate(s.started_at)?.toLocaleDateString(undefined, { month: "short", day: "numeric" }) || "Unknown date";

  function select(index) {
    selected = index;
    [...plot.children].forEach((button, i) => {
      button.classList.toggle("selected", i === selected);
      button.setAttribute("aria-pressed", String(i === selected));
    });
    const s = recent[selected];
    setChildren(detail,
      h("div", { class: "trend-session" }, h("strong", null, s.name || "Workout"), h("span", null, date(s))),
      h("strong", { class: "trend-value" }, metric.format(s[metric.key] || 0)),
      h("button", { class: "icon-btn ghost", "aria-label": `View ${s.name || "workout"} on ${date(s)}`, onClick: () => navigate(`/history/session/${s.id}`) }, icon("forward", { size: 18 })));
  }

  function draw() {
    for (const button of options.children) {
      const active = button.dataset.metric === metric.key;
      button.classList.toggle("active", active);
      button.setAttribute("aria-pressed", String(active));
    }
    const max = Math.max(1, ...recent.map(s => s[metric.key] || 0));
    scale.textContent = metric.format(max);
    setChildren(plot, ...recent.map((s, i) => {
      const value = s[metric.key] || 0;
      return h("button", {
        class: "activity-bar", title: `${date(s)} · ${s.name || "Workout"} · ${metric.format(value)}`,
        "aria-label": `${s.name || "Workout"}, ${date(s)}, ${metric.format(value)}`,
        onClick: () => select(i),
      }, h("span", { class: ["activity-bar-fill", !value && "zero"], style: { height: `${Math.max(2, value / max * 100)}%` } }));
    }));
    select(selected);
  }
  for (const m of metrics) options.appendChild(h("button", { dataset: { metric: m.key }, onClick: () => { metric = m; draw(); } }, m.label));
  root.append(h("div", { class: "trend-heading" }, h("span", { class: "dim" }, `Last ${recent.length} workout${recent.length === 1 ? "" : "s"}`), options), scale, plot,
    h("div", { class: "trend-axis", "aria-hidden": "true" }, h("span", null, date(recent[0])), h("span", null, date(recent[recent.length - 1]))), detail,
    h("p", { class: "trend-hint" }, "Each bar is a workout. Tap to explore."));
  draw();
  return root;
}
